import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { Calendar } from "src/primitives/Calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/primitives/Popover";
import { cn } from "src/shadcn/lib/utils";
import { useControlledOpen } from "src/hooks/useControlledOpen";
import { useCursorRestore } from "src/hooks/useCursorRestore";
import { useOutsideClickClose } from "src/hooks/useOutsideClickClose";

import type { DatePickerProps } from "./types";
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  INITIAL_TIME_VALUE,
  SIZE_CONFIG,
} from "./constants";
import {
  applyTimeToDate,
  toBrowserLocalDate,
  coerceDateValue,
  formatDate,
  getDatePlaceholder,
  getDisplayFormat,
  getInitialCalendarMonth,
  isDatePartComplete,
  isMonthDisabled,
  isYearDisabled,
  orderRange,
  parseDate,
  parseRangeText,
  startOfWeek,
  toNativeDate,
  toUserTzDayjs,
  weekDaysFrom,
} from "./utils";
import {
  applyMask,
  buildMaskTemplate,
  buildRangeMaskTemplate,
  isFixedWidthFormat,
} from "src/utils/inputMask";
import { DatePickerFooter } from "./DatePickerFooter";
import { DatePickerField } from "./DatePickerField";
import { DatePickerTrigger } from "./DatePickerTrigger";
import { MonthPicker } from "./MonthPicker";
import { RangeCalendar } from "./RangeCalendar";
import { useRangePicker } from "./useRangePicker";
import { YearPicker } from "./YearPicker";
import { TimePickerPanel } from "../TimePicker/TimePickerPanel";
import { dateToTimeValue } from "../TimePicker/utils";

const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      onBlur,
      type = "date",
      dateFormat = DEFAULT_DATE_FORMAT,
      timeFormat = DEFAULT_TIME_FORMAT,
      showTime = false,
      timePickerFormat,
      showSeconds = false,
      minDate,
      maxDate,
      disabledDate,
      placeholder,
      label,
      error,
      helpText,
      size = "medium",
      disabled = false,
      allowClear = true,
      required = false,
      timezone,
      onTimezoneChange,
      onOk,
      needConfirm = false,
      open: openProp,
      onOpenChange,
      className,
      labelProps,
      picker = "date",
      side = "bottom",
      align = "start",
      trigger,
      weekStartsOn = 0,
    },
    ref
  ) => {
    const generatedId = useId();
    const errorId = `error_${generatedId}`;
    const helpTextId = `helpText_${generatedId}`;
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const cursorPosRef = useRef<number | null>(null);

    const popoverContentId = useRef(
      `datepicker-popover-${generatedId}`
    ).current;

    const { open, setOpen } = useControlledOpen(openProp, onOpenChange);

    const [internalValue, setInternalValue] = useState(
      coerceDateValue(defaultValue, type) ?? null
    );

    const [calendarMonth, setCalendarMonth] = useState(() =>
      getInitialCalendarMonth(value, defaultValue, type)
    );

    const [pendingDate, setPendingDate] = useState<Date | null>(null);
    const [pendingTime, setPendingTime] = useState(INITIAL_TIME_VALUE);
    const [pendingTimeTo, setPendingTimeTo] = useState(INITIAL_TIME_VALUE);

    const currentValue = useMemo(
      () =>
        value !== undefined ? coerceDateValue(value, type) : internalValue,
      [value, type, internalValue]
    );

    const normalizedMinDate = useMemo(
      () => toNativeDate(minDate) ?? undefined,
      [minDate]
    );
    const normalizedMaxDate = useMemo(
      () => toNativeDate(maxDate) ?? undefined,
      [maxDate]
    );

    const displayFormat = getDisplayFormat(dateFormat, timeFormat, showTime);
    const sizeConfig = SIZE_CONFIG[size];
    const maskEnabled = isFixedWidthFormat(displayFormat);
    const effectiveTimePickerFormat: "12" | "24" =
      timePickerFormat ?? (/\ba\b/i.test(timeFormat) ? "12" : "24");

    const defaultPlaceholder = getDatePlaceholder(
      dateFormat,
      timeFormat,
      showTime,
      type
    );

    const maskTemplate = useMemo(
      () =>
        maskEnabled
          ? type === "range"
            ? buildRangeMaskTemplate(displayFormat)
            : buildMaskTemplate(displayFormat)
          : null,
      [displayFormat, type, maskEnabled]
    );

    const singleDateLen = useMemo(
      () => buildMaskTemplate(displayFormat).pattern.length,
      [displayFormat]
    );

    const calendarDisabled = useCallback(
      (date: Date) => {
        if (disabledDate?.(date)) return true;
        if (
          normalizedMinDate &&
          date < new Date(new Date(normalizedMinDate).setHours(0, 0, 0, 0))
        )
          return true;
        if (
          normalizedMaxDate &&
          date > new Date(new Date(normalizedMaxDate).setHours(23, 59, 59, 999))
        )
          return true;

        return false;
      },
      [normalizedMinDate, normalizedMaxDate, disabledDate]
    );

    const getDisplayText = useCallback(() => {
      if (type !== "range") {
        return formatDate(currentValue as Date | null, displayFormat);
      }

      const rangeValue = currentValue as [Date | null, Date | null] | null;
      if (!rangeValue || (!rangeValue[0] && !rangeValue[1])) return "";

      const from = rangeValue[0]
        ? formatDate(rangeValue[0], displayFormat)
        : "";
      const to = rangeValue[1] ? formatDate(rangeValue[1], displayFormat) : "";

      return `${from} - ${to}`;
    }, [currentValue, displayFormat, type]);

    const [inputText, setInputText] = useState(() => getDisplayText());

    const commitValue = (date: Date | [Date, Date] | null) => {
      if (date === null) {
        setInternalValue(null);
        if (type === "range") {
          onChange?.([null, null], ["", ""]);
        } else {
          onChange?.(null, "");
        }
        setInputText("");

        return;
      }

      const toSerializableDayjs = (d: Date) => {
        const userTzDayjs = toUserTzDayjs(d);
        if (showTime) return userTzDayjs;

        return userTzDayjs.hour(12).minute(0).second(0).millisecond(0);
      };

      if (Array.isArray(date)) {
        setInternalValue(date);
        const formatted: [string, string] = [
          formatDate(date[0], displayFormat),
          formatDate(date[1], displayFormat),
        ];
        onChange?.(
          [toSerializableDayjs(date[0]), toSerializableDayjs(date[1])],
          formatted
        );
        setInputText(`${formatted[0]} - ${formatted[1]}`);

        return;
      }

      setInternalValue(date);
      const formatted = formatDate(date, displayFormat);
      onChange?.(toSerializableDayjs(date), formatted);
      setInputText(formatted);
    };

    const {
      pendingRange,
      rangeSelectionStep,
      setRangeSelectionStep,
      handleRangeSelect,
      handleHoverIn,
      handleHoverOut,
      memoizedRangeSelected,
      getPreviewParts,
      resetTransientState,
    } = useRangePicker({
      currentValue,
      showTime,
      needConfirm,
      commitValue,
      setOpen,
    });

    useEffect(() => {
      if (type !== "range" || !open) return;

      const { from, to } = getPreviewParts();
      const fromWithTime =
        from && showTime ? applyTimeToDate(from, pendingTime) : from;
      const toWithTime =
        to && showTime ? applyTimeToDate(to, pendingTimeTo) : to;
      const fromText = fromWithTime
        ? formatDate(fromWithTime, displayFormat)
        : "";
      const toText = toWithTime ? formatDate(toWithTime, displayFormat) : "";

      setInputText(fromText || toText ? `${fromText} - ${toText}` : "");
    }, [
      type,
      open,
      getPreviewParts,
      displayFormat,
      showTime,
      pendingTime,
      pendingTimeTo,
    ]);

    const parseAndApplyRange = (text: string, cursorPos: number | null) => {
      const parts = text.split(" - ");
      const separatorIdx = text.indexOf(" - ");
      const editingTo =
        separatorIdx >= 0 && cursorPos !== null && cursorPos > separatorIdx + 2;

      if (editingTo) {
        const to = parseDate(parts[1] ?? "", displayFormat);
        if (to) setCalendarMonth(to);
      } else {
        if (!isDatePartComplete(parts[0] ?? "", maskEnabled, singleDateLen))
          return;

        const from = parseDate(parts[0] ?? "", displayFormat);
        if (from) setCalendarMonth(from);
      }

      const range = parseRangeText(
        text,
        displayFormat,
        maskEnabled,
        singleDateLen
      );

      if (range) commitValue(range);
    };

    const commitPendingOnClose = useCallback(() => {
      const hasOkButton = showTime || needConfirm;
      if (!hasOkButton || !pendingDate) {
        setInputText(getDisplayText());

        return;
      }

      const finalDate = showTime
        ? applyTimeToDate(pendingDate, pendingTime)
        : pendingDate;

      const currentSingle =
        type === "date" ? (currentValue as Date | null) : null;

      const isSame =
        currentSingle && finalDate.getTime() === currentSingle.getTime();

      if (isSame) {
        setInputText(getDisplayText());

        return;
      }

      commitValue(finalDate);
    }, [
      showTime,
      needConfirm,
      pendingDate,
      pendingTime,
      type,
      currentValue,
      getDisplayText,
      onChange,
    ]);

    const closePopover = useCallback(() => {
      if (type === "range" && pendingRange?.[0] && pendingRange?.[1]) {
        const [fromBase, toBase] = orderRange(
          pendingRange[0],
          pendingRange[1]
        ) as [Date, Date];
        const fromFinal = showTime
          ? applyTimeToDate(fromBase, pendingTime)
          : fromBase;
        const toFinal = showTime
          ? applyTimeToDate(toBase, pendingTimeTo)
          : toBase;
        commitValue([fromFinal, toFinal]);
      } else {
        commitPendingOnClose();
      }
      resetTransientState();
      setOpen(false);
    }, [
      commitPendingOnClose,
      setOpen,
      type,
      pendingRange,
      pendingTime,
      pendingTimeTo,
      showTime,
      resetTransientState,
    ]);

    useOutsideClickClose({
      enabled: open,
      containerRef,
      popoverElementId: popoverContentId,
      onClose: closePopover,
    });

    const openPopover = () => {
      setOpen(true);
      resetTransientState();

      if (type === "range") {
        const rangeVal = currentValue as [Date | null, Date | null] | null;
        const fromDate = rangeVal?.[0] ?? null;
        const toDate = rangeVal?.[1] ?? null;
        setPendingDate(null);
        setPendingTime(
          fromDate ? dateToTimeValue(fromDate) : INITIAL_TIME_VALUE
        );
        setPendingTimeTo(toDate ? dateToTimeValue(toDate) : INITIAL_TIME_VALUE);

        return;
      }

      const dateVal = currentValue as Date | null;
      if (!dateVal) {
        setPendingDate(null);
        setPendingTime(INITIAL_TIME_VALUE);

        return;
      }

      setCalendarMonth(dateVal);
      setPendingDate(dateVal);
      setPendingTime(dateToTimeValue(dateVal));
    };

    const handleDateSelect = (selected: Date | undefined) => {
      if (!selected) return;

      setCalendarMonth(selected);

      if (showTime || needConfirm) {
        setPendingDate(selected);

        return;
      }

      commitValue(selected);
      setOpen(false);
    };

    const handleNow = () => {
      const now = toBrowserLocalDate(new Date());

      if (showTime || needConfirm) {
        setPendingDate(now);
        setPendingTime(dateToTimeValue(now));

        return;
      }

      commitValue(now);
      setOpen(false);
    };

    const handleOk = () => {
      if (type === "range" && (showTime || needConfirm)) {
        const committedRange = currentValue as
          | [Date | null, Date | null]
          | null;
        const fromValue = pendingRange?.[0] ?? committedRange?.[0] ?? null;
        const toValue = pendingRange?.[1] ?? committedRange?.[1] ?? null;

        if (rangeSelectionStep === "from" && fromValue) {
          setRangeSelectionStep("to");
          setCalendarMonth(
            new Date(fromValue.getFullYear(), fromValue.getMonth(), 1)
          );
          if (showTime && pendingRange?.[0]) {
            setPendingTimeTo(INITIAL_TIME_VALUE);
          }

          return;
        }

        if (rangeSelectionStep === "to" && fromValue && toValue) {
          const [fromBase, toBase] = orderRange(fromValue, toValue) as [
            Date,
            Date,
          ];
          const fromFinal = showTime
            ? applyTimeToDate(fromBase, pendingTime)
            : fromBase;
          const toFinal = showTime
            ? applyTimeToDate(toBase, pendingTimeTo)
            : toBase;
          commitValue([fromFinal, toFinal]);
          onOk?.([fromFinal, toFinal]);
          resetTransientState();
          setOpen(false);

          return;
        }

        return;
      }

      if (pendingDate) {
        const finalDate = showTime
          ? applyTimeToDate(pendingDate, pendingTime)
          : pendingDate;

        commitValue(finalDate);
        onOk?.(finalDate);
      }
      setOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      commitValue(null);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let text = e.target.value;

      if (maskTemplate) {
        const masked = applyMask(
          text,
          maskTemplate,
          inputText,
          e.target.selectionStart
        );

        text = masked.text;
        cursorPosRef.current = masked.cursorPosition;
      }

      setInputText(text);

      if (type === "range") {
        parseAndApplyRange(text, e.target.selectionStart);

        return;
      }

      const isComplete = maskTemplate
        ? text.length >= maskTemplate.pattern.length
        : true;

      if (!isComplete) return;

      const parsed = parseDate(text, displayFormat);
      if (!parsed) return;

      setCalendarMonth(parsed);

      if (showTime || needConfirm) {
        setPendingDate(parsed);
        setPendingTime(dateToTimeValue(parsed));

        return;
      }

      commitValue(parsed);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        setPendingDate(null);
        setPendingTime(INITIAL_TIME_VALUE);
        setInputText(getDisplayText());
        setOpen(false);

        return;
      }

      if (e.key !== "Enter") return;

      if (type === "range") {
        const range = parseRangeText(
          inputText,
          displayFormat,
          maskEnabled,
          singleDateLen
        );
        if (range) commitValue(range);
      } else {
        const parsed = parseDate(inputText, displayFormat);
        if (parsed) commitValue(parsed);
      }

      setOpen(false);
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const next = e.relatedTarget as HTMLElement | null;
      if (next && containerRef.current?.contains(next)) return;

      const popoverEl = document.getElementById(popoverContentId);
      if (next && popoverEl?.contains(next)) return;

      onBlur?.(e);
    };

    useEffect(() => {
      if (!open) setInputText(getDisplayText());
    }, [getDisplayText, open]);

    useCursorRestore([{ inputRef, cursorRef: cursorPosRef }]);

    useEffect(() => {
      if (!open || type !== "date") return;

      const dateVal = currentValue as Date | null;
      if (dateVal instanceof Date) setCalendarMonth(dateVal);
    }, [open]);

    const hasField = !!(label || error || helpText);
    const showFooter = showTime || needConfirm || !!onTimezoneChange;
    const todayDate = toBrowserLocalDate(new Date());

    const handleGridPickerSelect = (date: Date) => {
      commitValue(date);
      setOpen(false);
    };

    const handleSingleDateSelectWithPicker = (selected: Date | undefined) => {
      if (!selected) return handleDateSelect(undefined);

      handleDateSelect(
        picker === "week" ? startOfWeek(selected, weekStartsOn) : selected
      );
    };

    const weekHighlightProps = (() => {
      if (picker !== "week" || type !== "date" || !currentValue) return {};

      const weekStart = startOfWeek(currentValue as Date, weekStartsOn);
      const weekDays = weekDaysFrom(weekStart);

      return {
        modifiers: {
          weekStart: [weekDays[0]],
          weekMiddle: weekDays.slice(1, 6),
          weekEnd: [weekDays[6]],
        },
        modifiersClassNames: {
          weekStart:
            "!bg-primary !text-primary-foreground !rounded-e-none hover:!bg-primary",
          weekMiddle:
            "!bg-primary !text-primary-foreground !rounded-none hover:!bg-primary",
          weekEnd:
            "!bg-primary !text-primary-foreground !rounded-s-none hover:!bg-primary",
        },
      };
    })();

    const weekRowHoverClassName =
      picker === "week"
        ? "[&_button]:!transition-none [&:hover_button]:!bg-primary [&:hover_button]:!text-primary-foreground [&:hover_button]:!rounded-none [&:hover_button]:cursor-pointer"
        : undefined;

    const ariaDescribedBy =
      [error ? errorId : null, helpText ? helpTextId : null]
        .filter(Boolean)
        .join(" ") || undefined;

    const calendarSelected = () => {
      if (showTime || needConfirm) {
        return pendingDate ?? (currentValue as Date | undefined) ?? undefined;
      }

      return (currentValue as Date | undefined) ?? undefined;
    };

    const handleTriggerOpenChange = (nextOpen: boolean) => {
      if (nextOpen === open) return;

      if (nextOpen) openPopover();
      else closePopover();
    };

    const triggerContent = (
      <Popover
        open={open}
        onOpenChange={trigger ? handleTriggerOpenChange : undefined}
      >
        {trigger ? (
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        ) : (
          <DatePickerTrigger
            containerRef={containerRef}
            inputRef={inputRef}
            disabled={disabled}
            error={error}
            hasField={hasField}
            className={className}
            triggerSizeClass={sizeConfig.trigger}
            inputSizeClass={sizeConfig.input}
            iconSizeClass={sizeConfig.icon}
            label={label}
            ariaDescribedBy={ariaDescribedBy}
            placeholder={placeholder ?? defaultPlaceholder}
            inputText={inputText}
            allowClear={allowClear}
            timezone={timezone}
            onInputChange={handleInputChange}
            onInputKeyDown={handleInputKeyDown}
            onInputFocus={() => {
              if (!open) openPopover();
            }}
            onInputBlur={handleInputBlur}
            onClear={handleClear}
          />
        )}
        {open && (
          <PopoverContent
            id={popoverContentId}
            className="w-auto p-0"
            align={align}
            side={side}
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={e => e.preventDefault()}
            onPointerDownOutside={e => e.preventDefault()}
            onInteractOutside={e => e.preventDefault()}
          >
            <div className={cn("flex", showTime && "flex-row")}>
              {type === "date" && picker === "month" ? (
                <MonthPicker
                  value={(currentValue as Date | null) ?? null}
                  displayedYear={calendarMonth.getFullYear()}
                  onYearChange={year =>
                    setCalendarMonth(
                      new Date(year, calendarMonth.getMonth(), 1)
                    )
                  }
                  onSelect={handleGridPickerSelect}
                  isDisabled={date =>
                    isMonthDisabled(date, normalizedMinDate, normalizedMaxDate)
                  }
                />
              ) : type === "date" && picker === "year" ? (
                <YearPicker
                  value={(currentValue as Date | null) ?? null}
                  displayedYear={calendarMonth.getFullYear()}
                  onYearChange={year => setCalendarMonth(new Date(year, 0, 1))}
                  onSelect={handleGridPickerSelect}
                  isDisabled={date =>
                    isYearDisabled(date, normalizedMinDate, normalizedMaxDate)
                  }
                />
              ) : type === "date" ? (
                <Calendar
                  mode="single"
                  captionLayout="dropdown"
                  today={todayDate}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  selected={calendarSelected()}
                  onSelect={handleSingleDateSelectWithPicker}
                  disabled={calendarDisabled}
                  weekStartsOn={weekStartsOn}
                  classNames={
                    weekRowHoverClassName
                      ? { week: cn("mt-2 flex w-full", weekRowHoverClassName) }
                      : undefined
                  }
                  {...weekHighlightProps}
                  {...(normalizedMinDate && { fromDate: normalizedMinDate })}
                  {...(normalizedMaxDate && { toDate: normalizedMaxDate })}
                />
              ) : showTime ? (
                <>
                  <RangeCalendar
                    todayDate={todayDate}
                    weekStartsOn={weekStartsOn}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    selected={memoizedRangeSelected}
                    onSelect={handleRangeSelect}
                    disabled={calendarDisabled}
                    fromDate={normalizedMinDate}
                    toDate={normalizedMaxDate}
                    onHoverIn={handleHoverIn}
                    onHoverOut={handleHoverOut}
                    numberOfMonths={1}
                  />
                  <div className="border-inline-start border-border pt-2">
                    <TimePickerPanel
                      value={
                        rangeSelectionStep === "from"
                          ? pendingTime
                          : pendingTimeTo
                      }
                      onChange={
                        rangeSelectionStep === "from"
                          ? setPendingTime
                          : setPendingTimeTo
                      }
                      format={effectiveTimePickerFormat}
                      showSeconds={showSeconds}
                      disabled={disabled}
                    />
                  </div>
                </>
              ) : (
                <RangeCalendar
                  todayDate={todayDate}
                  weekStartsOn={weekStartsOn}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  selected={memoizedRangeSelected}
                  onSelect={handleRangeSelect}
                  disabled={calendarDisabled}
                  fromDate={normalizedMinDate}
                  toDate={normalizedMaxDate}
                  onHoverIn={handleHoverIn}
                  onHoverOut={handleHoverOut}
                />
              )}
              {showTime && type !== "range" && (
                <div className="border-inline-start border-border">
                  <TimePickerPanel
                    value={pendingTime}
                    onChange={setPendingTime}
                    format={effectiveTimePickerFormat}
                    showSeconds={showSeconds}
                    disabled={disabled}
                  />
                </div>
              )}
            </div>
            {showFooter && (
              <DatePickerFooter
                showTime={showTime}
                needConfirm={needConfirm}
                timezone={timezone}
                onTimezoneChange={onTimezoneChange}
                onNow={handleNow}
                onOk={handleOk}
                okLabel={
                  type === "range" &&
                  (showTime || needConfirm) &&
                  rangeSelectionStep === "from"
                    ? "Next"
                    : "OK"
                }
              />
            )}
          </PopoverContent>
        )}
      </Popover>
    );

    if (!hasField) {
      return (
        <div ref={ref} className={className}>
          {triggerContent}
        </div>
      );
    }

    return (
      <DatePickerField
        ref={ref}
        disabled={disabled}
        error={error}
        className={className}
        label={label}
        required={required}
        helpText={helpText}
        labelProps={labelProps}
        errorId={errorId}
        helpTextId={helpTextId}
      >
        {triggerContent}
      </DatePickerField>
    );
  }
);

DatePicker.displayName = "DatePicker";

export { DatePicker };
